/**
 * Cart drawer recommendations (IWB-003)
 *
 * The cart drawer's "You Might Also Like" row is a Klevu recommendation widget.
 * This module is the one place that gives it the cart's products, so a later
 * replacement for Klevu swaps this file. Decision record:
 * docs/adr/0001-cart-recommendations-module.md
 *
 * Klevu calls window.klevu_processRECSRecordQueries(recordQueries, recsKey) from
 * its request-build step whenever that function exists. For the drawer's widget
 * only, this module adds the cart's variants to settings.context.recentObjects and
 * the cart's products to settings.excludeIds, the same shape Klevu's own
 * bought-together step builds from the cart on a cart page. After a cart change it
 * runs the widget again with powerUp(). The row hides while the cart is empty.
 */
(() => {
  if (window.cartDrawerRecs) return;

  const initialRow = document.querySelector('#cartDrawer .klevu-recs');
  if (!initialRow || !initialRow.id) return;

  const recsKey = initialRow.id;
  const hiddenProductIds = (window.cartHiddenProductList || []).map(String);
  const ownEntries = new WeakSet();

  // Cart lines as { variantIds, productIds }, without hidden lines such as the
  // minimum order charge.
  const fromLines = (lines) => {
    const variantIds = [];
    const productIds = [];
    lines.forEach(({ variantId, productId }) => {
      if (!variantId || hiddenProductIds.includes(String(productId))) return;
      if (!variantIds.includes(String(variantId))) variantIds.push(String(variantId));
      if (productId && !productIds.includes(String(productId))) productIds.push(String(productId));
    });
    return { variantIds, productIds };
  };

  const fromCartJson = (cartJson) => fromLines(cartJson.items.map((item) => ({ variantId: item.variant_id, productId: item.product_id })));

  const readInitialCart = () => {
    const el = document.querySelector('script[data-cart-drawer-recs-cart]');
    if (!el) return { variantIds: [], productIds: [] };
    try {
      const data = JSON.parse(el.textContent);
      return fromLines((data.variantIds || []).map((variantId, i) => ({ variantId, productId: (data.productIds || [])[i] })));
    } catch (error) {
      console.error('cart-drawer-recs could not read the initial cart', error);
      return { variantIds: [], productIds: [] };
    }
  };

  const signatureOf = (c) => c.variantIds.slice().sort().join(',');

  let cart = readInitialCart();
  let signature = signatureOf(cart);
  let lastRequestSignature = null;
  let refreshTimer = null;
  // Each cart read gets a number, and only the newest one may change the cart, so
  // a slow /cart.js answer never overwrites a newer cartUpdate.
  let cartReadId = 0;

  const getRow = () => document.getElementById(recsKey);

  const getList = () => {
    const list = window.klevu && window.klevu.recs && window.klevu.recs.list;
    if (!Array.isArray(list)) return null;
    return list.find((item) => {
      const scope = item.getScope && item.getScope();
      return scope && scope.element && scope.element.id === recsKey;
    }) || null;
  };

  const setVisibility = () => {
    const row = getRow();
    if (row) row.classList.toggle('hide', cart.variantIds.length === 0);
  };

  const previousHook = window.klevu_processRECSRecordQueries;
  window.klevu_processRECSRecordQueries = function (recordQueries, key) {
    if (typeof previousHook === 'function') previousHook.apply(this, arguments);
    if (key !== recsKey) return;

    lastRequestSignature = signature;
    if (!cart.variantIds.length || !Array.isArray(recordQueries)) return;

    const constants = window.klevu && window.klevu.settings && window.klevu.settings.constants;
    const queryId = constants ? constants.RECS_QUERY_ID : null;

    recordQueries.forEach((query) => {
      if (!query || (queryId && query.id !== queryId)) return;
      query.settings = query.settings || {};
      query.settings.context = query.settings.context || {};

      const cartRecords = { typeOfRecord: 'KLEVU_PRODUCT', records: cart.variantIds.map((id) => ({ id })) };
      ownEntries.add(cartRecords);
      const recent = (query.settings.context.recentObjects || []).filter((entry) => !ownEntries.has(entry));
      recent.push(cartRecords);
      query.settings.context.recentObjects = recent;

      const exclude = (query.settings.excludeIds || []).filter((entry) => !ownEntries.has(entry));
      cart.productIds.forEach((value) => {
        const entry = { key: 'itemGroupId', value };
        ownEntries.add(entry);
        exclude.push(entry);
      });
      query.settings.excludeIds = exclude;
    });
  };

  // Repeated powerUp() renders one fresh row without duplicating Klevu's
  // request steps (gate 1 test, 2026-10-10).
  const refresh = () => {
    if (!cart.variantIds.length || lastRequestSignature === signature) return;
    const list = getList();
    if (list) list.powerUp();
  };

  const setCart = (next) => {
    cart = next;
    setVisibility();
    const nextSignature = signatureOf(cart);
    if (nextSignature === signature) return;
    signature = nextSignature;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 250);
  };

  const fetchCart = () => {
    const readId = ++cartReadId;
    window.fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart.js`)
      .then((response) => response.json())
      .then((cartJson) => {
        if (readId !== cartReadId) return;
        setCart(fromCartJson(cartJson));
      })
      .catch((error) => console.error('cart-drawer-recs could not read /cart.js', error));
  };

  // If Klevu built the row before this module defined its hook, run it once
  // more with the cart.
  const catchUp = (attempt = 0) => {
    if (lastRequestSignature === signature) return;
    const list = getList();
    const scope = list && list.getScope();
    if (scope && scope.viewPayload) {
      refresh();
      return;
    }
    if (attempt < 40) setTimeout(() => catchUp(attempt + 1), 500);
  };

  // PUB_SUB_EVENTS is a script-level const (snippets/js-head-settings.liquid), not a window property.
  if (typeof window.subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const data = event && event.cartData;
      // Only full cart objects (from /cart.js) carry item_count.
      if (data && Array.isArray(data.items) && typeof data.item_count === 'number') {
        cartReadId++;
        setCart(fromCartJson(data));
      }
    });
  }
  document.addEventListener('cart-drawer-recs-reload', fetchCart);

  setVisibility();
  catchUp();

  window.cartDrawerRecs = {
    recsKey,
    refresh: fetchCart,
    getCart: () => ({ variantIds: cart.variantIds.slice(), productIds: cart.productIds.slice() }),
  };
})();
